/**
 * 平台 API 客户端（§3.3 响应包的浏览器侧对偶）。
 * ★ 三条纪律：
 *   1) 只有 `{ok:true}` 才算成功；`error.code` 原样抛给上层做 i18n 文案映射（前端不翻译错误码，也不泄露 msgKey 之外的信息）
 *   2) locale 一律显式传（平台侧对未安装语言回 422，不静默回落 ⇒ 这里必须能区分"没模板"和"语言没装"）
 *   3) 网络/5xx 与业务错误分开：前者可重试（列表页显示"重试"），后者重试无意义
 */
import type {
  BindingItem,
  BindResult,
  CategoryNode,
  DeploymentItem,
  HealthPayload,
  LicenseRow,
  LocaleOption,
  MePayload,
  OrderRow,
  PageMeta,
  Prerequisites,
  ProfilePayload,
  SessionPayload,
  SiteItem,
  SupportTicketItem,
  TagItem,
  TemplateDetail,
  TemplateListItem,
  UsageLogItem,
} from './types';

export const API_BASE: string = (import.meta.env?.VITE_API_BASE ?? 'http://127.0.0.1:8787').replace(/\/$/, '');

/** 业务错误：带平台错误码与 HTTP 状态（status 用于"是否值得重试"的判断） */
export class ApiFailure extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(code: string, status: number, details: Record<string, unknown> = {}) {
    super(`${code} (${status})`);
    this.name = 'ApiFailure';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** 429/5xx/网络失败 = 可重试；401/403/404/422 = 重试也是白试 */
  get retryable(): boolean {
    return this.status === 429 || this.status >= 500 || this.status === 0;
  }
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  meta?: PageMeta;
  /** ★ 服务端 Err 的错误参数名是 `params`（shared/envelope.ts `fail(code, params)`），
   *   写成 `details` 的话类型照样过、运行时永远拿到空对象——「缺哪个权限」「语言支持哪些 locale」
   *   这类必须展示给用户的细节就会静默消失。字段名以服务端为准。
   */
  error?: { code?: string; msgKey?: string; params?: Record<string, unknown> };
}

export interface Paged<T> {
  items: T[];
  meta?: PageMeta;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ClientOptions {
  baseUrl?: string;
  fetchImpl?: FetchLike;
  token?: () => string | null;
}

export function createClient(options: ClientOptions = {}) {
  const baseUrl = (options.baseUrl ?? API_BASE).replace(/\/$/, '');
  const doFetch: FetchLike = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const tokenOf = options.token ?? (() => null);

  /**
   * 一次请求 → 已校验过的信封。
   * ★ 拆成单独一层是因为：**列表接口返 `data: []`，详情/登录/元信息接口返 `data: {}`**。
   *   以前只有 `call()` 一个出口、一律 `data as T[]`，于是详情页第 0 项永远是 undefined
   *   （整页空白且不报错）、A1 前置要求永远落到“连不上 API”、买家登录抱 EMPTY_SESSION。
   */
  async function envelope<T>(path: string, init: RequestInit = {}): Promise<Envelope<T>> {
    const headers = new Headers(init.headers ?? {});
    if (init.body !== undefined) headers.set('content-type', 'application/json');
    const token = tokenOf();
    if (token) headers.set('authorization', `Bearer ${token}`);
    let res: Response;
    try {
      res = await doFetch(`${baseUrl}${path}`, { ...init, headers });
    } catch (e) {
      // ★ 网络层失败（CORS/断网/服务未起）与业务失败必须分开：否则页面只会显示"没有模板"
      throw new ApiFailure('NETWORK_ERROR', 0, { cause: String(e instanceof Error ? e.message : e), path });
    }
    const env = (await res.json().catch(() => null)) as Envelope<T> | null;
    if (!res.ok || !env || env.ok !== true) {
      const code = env?.error?.code ?? `HTTP_${res.status}`;
      throw new ApiFailure(code, res.status, (env?.error?.params ?? {}) as Record<string, unknown>);
    }
    return env;
  }

  /** 列表类接口（`data` 是数组，分页信息在 `meta`） */
  async function call<T>(path: string, init: RequestInit = {}): Promise<Paged<T>> {
    const env = await envelope<T[]>(path, init);
    return { items: (env.data ?? []) as T[], meta: env.meta };
  }

  /** 单对象接口（详情 / 登录会话 / 前置要求 / 当前用户）：★ 绝不过 `items[0]` 取数 */
  async function callOne<T>(path: string, init: RequestInit = {}): Promise<T> {
    const env = await envelope<T>(path, init);
    return env.data as T;
  }

  const qs = (params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') search.set(k, String(v));
    }
    const s = search.toString();
    return s ? `?${s}` : '';
  };

  return {
    baseUrl,
    locales: () => call<LocaleOption>('/api/locales'),
    prerequisites: () => callOne<Prerequisites>('/api/meta/prerequisites'),
    categories: (locale: string) => call<CategoryNode>(`/api/categories${qs({ locale })}`),
    tags: (locale: string) => call<TagItem>(`/api/tags${qs({ locale })}`),
    templates: (p: {
      locale: string;
      category?: string;
      tag?: string;
      q?: string;
      featured?: string;
      currency?: string;
      cursor?: string;
      limit?: number;
    }) => call<TemplateListItem>(`/api/templates${qs({ ...p })}`),
    template: (slug: string, locale: string) => callOne<TemplateDetail>(`/api/templates/${slug}${qs({ locale })}`),

    /** 登录态：CTA「立即购买」需要身份，结算/部署向导在迭代二接入，这里只保证拿得到真实会话 */
    login: (email: string, password: string) =>
      callOne<SessionPayload>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    /** ★ 密码强度门槛与平台 registerSchema 一致（≥10 位）：前置提示而不是等 422 */
    register: (email: string, password: string) =>
      callOne<{ userId: string; verifyToken?: string }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    /** dev 环境注册接口会回显 verifyToken（production 不返回），用它直接过邮箱验证 */
    verifyEmail: (token: string) =>
      callOne<{ verified: boolean }>('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
    me: () => callOne<MePayload>('/api/auth/me'),

    /** B2 我的订单 / B3 我的授权（列表类，data 是裸数组、分页在 meta） */
    orders: (limit = 20) => call<OrderRow>(`/api/orders${qs({ limit })}`),
    licenses: () => call<LicenseRow>('/api/licenses'),

    /**
     * 用户中心（B1/B4/B5/B6/B7/B9/B11/B12）。
     * ★ 全部需要登录态：这里的每个方法都假定 `token()` 已就绪；未登录时服务端回 401，
     *   视图层负责先判 `appStore.state.me` 再决定是否调用（避免用 401 刷日志）。
     */
    account: {
      profile: () => callOne<ProfilePayload>('/api/me/profile'),
      updateProfile: (body: { nickname?: string | null; companyName?: string | null; defaultLocale?: string }) =>
        callOne<{ updated: boolean }>('/api/me/profile', { method: 'PATCH', body: JSON.stringify(body) }),
      changePassword: (body: { currentPassword: string; newPassword: string }) =>
        callOne<{ changed: boolean; reauthRequired: boolean }>('/api/me/password', {
          method: 'POST',
          body: JSON.stringify(body),
        }),

      /** B5 站点清单（含 B6 双版本线 / B7 额度快照） */
      sites: () => callOne<{ items: SiteItem[]; counts: Record<string, number> }>('/api/sites'),
      /** B5 部署历史（列表；与 `/api/deployments/:id` 单条详情区分） */
      deployments: (limit = 20) => call<DeploymentItem>(`/api/deployments${qs({ limit })}`),
      /** B12 站点健康（platform / buyerReported 分栏） */
      health: () => callOne<HealthPayload>('/api/me/site-health'),
      /** B9 使用记录 */
      usageLogs: (limit = 20) =>
        callOne<{ items: UsageLogItem[]; limit: number }>(`/api/me/usage-logs${qs({ limit })}`),

      /** B7 某张授权下的绑定明细（★ 真实路径带 /me 前缀，且 data 是裸数组） */
      bindings: (licenseId: string) => call<BindingItem>(`/api/me/licenses/${licenseId}/bindings`),
      /** B4 绑定：Token 明文只在这一次请求体里出现，前端不做任何本地持久化 */
      bind: (
        licenseId: string,
        body: { cfAccountId: string; cfApiToken: string; tokenRetained: boolean; bindIndex?: number },
      ) => callOne<BindResult>(`/api/licenses/${licenseId}/bind`, { method: 'POST', body: JSON.stringify(body) }),
      unbind: (licenseId: string, bindingId: string) =>
        callOne<{ unbound: boolean }>(`/api/licenses/${licenseId}/bind/${bindingId}`, { method: 'DELETE' }),
      /** B7「不留存 Token」开关（★ 授权维度：一次切换作用于该授权下全部绑定） */
      setTokenRetention: (licenseId: string, retain: boolean) =>
        callOne<{ retained: boolean }>(`/api/licenses/${licenseId}/token-retention`, {
          method: 'POST',
          body: JSON.stringify({ retain }),
        }),

      /** B4/D7 触发部署（初始交付与升级共用同一接口，靠 versionLine/trigger 区分） */
      deploy: (body: {
        bindingId: string;
        versionLine?: 'template' | 'backend' | 'both';
        trigger?: 'initial' | 'template_update' | 'backend_update' | 'manual' | 'rollback' | 'rebind';
        templateVersion?: string;
        backendVersion?: string;
      }) =>
        callOne<{ deploymentId: string; status: string }>('/api/deployments', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      deployment: (id: string) =>
        callOne<DeploymentItem & { attempt: number; errorMsg: string | null }>(`/api/deployments/${id}`),
      /** B10 初始凭据一次性领取（★ 服务端领取即销毁） */
      claimCredentials: (deploymentId: string) =>
        callOne<{ bindingId: string; username: string; email: string; oneTimePassword: string }>(
          `/api/me/deployments/${deploymentId}/bootstrap-credentials`,
        ),
      siteAdminEntry: (bindingId: string) =>
        callOne<{
          bindingId: string;
          adminUrl: string | null;
          ready: boolean;
          bootstrapPending: boolean;
          credentialNotice: string;
        }>(`/api/me/site-admin-entry/${bindingId}`),

      /** B11 人工协助工单 */
      supportRequests: () => call<SupportTicketItem>('/api/support/requests'),
      createSupport: (body: {
        kind?: 'assist_bind' | 'deploy_failure' | 'token_permission' | 'other';
        severity?: 'low' | 'normal' | 'high';
        contact?: string;
        message: string;
        licenseId?: string;
        bindingId?: string;
      }) =>
        callOne<{ id: string; status: string; kindLabel: string; expectedFirstResponse: string }>(
          '/api/support/requests',
          { method: 'POST', body: JSON.stringify(body) },
        ),
    },
  };
}

export type ApiClient = ReturnType<typeof createClient>;

/**
 * 分 ⇒ 展示价（★ 币种符号由 locale 决定，不做「¥$」硬编码）。
 * 小数位：**整元不补零，非整元两侧都强制两位** —— 只给 maximumFractionDigits 的话
 * $49.50 会被 Intl 收成 "$49.5"（价格显示成半个分位，买家会当成写错了）。
 */
export function formatPrice(amountCents: number, currency: string, locale: string): string {
  const fraction = amountCents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(amountCents / 100);
}
